import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../hooks/useProfile';
import { supabase } from '../../services/supabase';
import NotificationToast from '../NotificationToast';
import DemoSwitcher from '../DemoSwitcher';
import { findOwnApplication, ONBOARDING_PENDING_STATUSES } from '../../lib/onboardingApplications';

const C = {
  navy:       '#0A1628',
  navyHover:  '#111E35',
  gold:       '#C9A84C',
  goldDim:    '#A8873A',
  white:      '#FFFFFF',
  surface:    '#F5F6F8',
  border:     '#D0D5DD',
  text:       '#0A0A0A',
  muted:      '#6B7280',
  sideText:   '#CBD5E1',
  sideMuted:  '#4B5A72',
  sideHover:  'rgba(255,255,255,0.05)',
  sideActive: 'rgba(201,168,76,0.12)',
};

const Icon = ({ name, size = 14, color = 'currentColor' }: { name: string; size?: number; color?: string }) => {
  const icons: Record<string, string> = {
    dashboard:  'M3 3h7v7H3zm11 0h7v7h-7zM3 14h7v7H3zm11 3h2v2h-2zm4 0h2v2h-2zm-4 4h2v2h-2zm4 0h2v2h-2z',
    altconnect: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 2-3 3-1.5-1.5M16 13a4 4 0 1 1 0-8 4 4 0 0 1 0 8z',
    ai:         'M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.636-6.364.707.707M12 21v-1M7 12a5 5 0 1 1 10 0c0 1.887-1.009 3.54-2.5 4.395V18a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-1.605C8.009 15.54 7 13.887 7 12z',
    compare:    'M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4M9 3h6M9 3v18m6-15h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M15 3v18',
    pipeline:   'M22 12h-4l-3 9L9 3l-3 9H2',
    calendar:   'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    messages:   'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
    peer:       'M18 20V10M12 20V4M6 20v-6',
    ddq:        'M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z',
    dataroom:   'M5 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4l2 2h4a2 2 0 0 1 2 2v1M5 19h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2z',
    funds:      'M12 1v22M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6',
    firm:       'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
    investors:  'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 2-3 3-1.5-1.5',
    team:       'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0z',
    settings:   'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
    admin:      'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    talent:     'M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zm-9 3a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm-2 8a4 4 0 0 1 8 0H9zm8-8h2M17 15h2',
    service:    'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
    event:      'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM9 16l2 2 4-4',
    pulse:      'M22 12h-4l-3 9L9 3l-3 9H2',
    intel:      'M3 3v18h18M7 15l3-4 3 3 5-7',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={icons[name] || icons.dashboard} />
    </svg>
  );
};

export default function AppLayout() {
  const { signOut } = useAuth();
  const { profile, firm, loading: profileLoading } = useProfile();
  const location = useLocation();
  const [gate, setGate] = useState<string | null>(null);
  const [pendingCount, setPendingCount]               = useState(0);
  const [unreadCount, setUnreadCount]                 = useState(0);
  const [pendingMeetingCount, setPendingMeetingCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount]       = useState(0);
  const [pendingAdminCount, setPendingAdminCount]     = useState(0);
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  const [providerId, setProviderId]                   = useState<string | null>(null);
  const [spPendingCount, setSpPendingCount]           = useState(0);
  const [spUnreadCount, setSpUnreadCount]             = useState(0);

  const isManager         = profile?.role === 'manager';
  const isAllocator       = profile?.role === 'allocator';
  const isServiceProvider = profile?.role === 'service_provider';
  const isCandidate       = profile?.role === 'candidate';
  const isSuperAdmin      = profile?.is_super_admin === true;

  const candidateNav = [
    { name: 'Dashboard',   path: '/candidate-dashboard',                     icon: 'dashboard',  show: true, group: 'PLATFORM' },
    { name: 'Job Board',   path: '/jobs',                                    icon: 'talent',     show: true, group: 'PLATFORM' },
    { name: 'My Profile',  path: '/candidate-dashboard?tab=profile',         icon: 'firm',       show: true, group: 'PLATFORM' },
    { name: 'Connections', path: '/candidate-dashboard?tab=connections',     icon: 'altconnect', show: true, group: 'PLATFORM' },
    { name: 'Messages',    path: '/candidate-dashboard?tab=connections',     icon: 'messages',   show: true, group: 'PLATFORM' },
    { name: 'Settings',    path: '/candidate-dashboard?tab=settings',        icon: 'settings',   show: true, group: 'MANAGE'   },
  ].filter(item => item.show);

  const serviceProviderNav = [
    { name: 'Dashboard',   path: '/service-provider-dashboard',                                    icon: 'dashboard',  show: true,           group: 'PLATFORM' },
    { name: 'My Profile',  path: providerId ? `/service-connect/provider/${providerId}` : '/service-provider-dashboard', icon: 'firm', show: true, group: 'PLATFORM' },
    { name: 'Connections', path: '/service-provider-dashboard?tab=connections',                    icon: 'altconnect', show: true,           group: 'PLATFORM' },
    { name: 'Messages',    path: '/service-provider-dashboard?tab=messages',                        icon: 'messages',   show: true,           group: 'PLATFORM' },
    { name: 'Settings',    path: '/profile-settings',                                              icon: 'settings',   show: true,           group: 'MANAGE'   },
  ].filter(item => item.show);

  const standardNav = [
    { name: 'Dashboard',       path: '/dashboard',                      icon: 'dashboard',  show: true,                group: 'PLATFORM' },
    { name: 'AltConnect',      path: '/alt-connect',                    icon: 'altconnect', show: isAllocator,         group: 'PLATFORM' },
    { name: 'Altveno AI',      path: '/alt-connect-ai',                 icon: 'ai',         show: isAllocator,         group: 'PLATFORM' },
    { name: 'Compare',         path: '/compare',                        icon: 'compare',    show: isAllocator,         group: 'PLATFORM' },
    { name: 'Altveno AI',      path: '/market-intelligence',            icon: 'ai',         show: isManager,           group: 'PLATFORM' },
    { name: 'Peer Analysis',   path: '/peer-analysis',                  icon: 'peer',       show: isManager,           group: 'PLATFORM' },
    { name: 'Pipeline',        path: '/pipeline',                       icon: 'pipeline',   show: true,                group: 'PLATFORM' },
    { name: 'Portfolio',       path: '/portfolio',                      icon: 'funds',      show: isAllocator,         group: 'PLATFORM' },
    { name: 'Calendar',        path: '/calendar',                       icon: 'calendar',   show: true,                group: 'PLATFORM' },
    { name: 'Messages',        path: '/messages',                       icon: 'messages',   show: true,                group: 'PLATFORM' },
    { name: 'DDQ Manager',     path: '/manage-ddq',                     icon: 'ddq',        show: isManager,           group: 'TOOLS'    },
    { name: 'DDQ Library',     path: '/ddq-library',                    icon: 'ddq',        show: isAllocator,         group: 'TOOLS'    },
    { name: 'Data Room',       path: '/data-room',                      icon: 'dataroom',   show: true,                group: 'TOOLS'    },
    { name: 'ServiceConnect',  path: '/service-connect',                icon: 'service',    show: isManager || isAllocator || isSuperAdmin, group: 'TOOLS' },
    { name: 'TalentConnect',   path: '/talent-connect',                 icon: 'talent',     show: isManager,           group: 'TOOLS'    },
    { name: 'Manage Funds',    path: '/manage-funds',                   icon: 'funds',      show: isManager,           group: 'MANAGE'   },
    { name: 'Manage Firm',     path: `/alt-connect/firm/${firm?.id}`,   icon: 'firm',       show: isManager && !!firm, group: 'MANAGE'   },
    { name: 'Manage Investors', path: '/manage-investors',              icon: 'investors',  show: isManager,           group: 'MANAGE'   },
    { name: 'Manage Team',     path: '/manage-team',                    icon: 'team',       show: isManager && !!firm, group: 'MANAGE'   },
    { name: 'Intelligence',    path: '/intelligence',                   icon: 'intel',      show: isSuperAdmin,        group: 'MANAGE'   },
    { name: 'Applications',    path: '/admin?tab=applications',         icon: 'ddq',        show: isSuperAdmin,        group: 'MANAGE'   },
    { name: 'Talent Admin',    path: '/pb-talent-admin',                icon: 'team',       show: isSuperAdmin,        group: 'MANAGE'   },
    { name: 'Survey Admin',    path: '/pb-survey-admin',                icon: 'pulse',      show: isSuperAdmin,        group: 'MANAGE'   },
    { name: 'Event Admin',     path: '/pb-event-admin',                 icon: 'event',      show: isSuperAdmin,        group: 'MANAGE'   },
    { name: 'Settings',        path: '/profile-settings',               icon: 'settings',   show: true,                group: 'MANAGE'   },
    { name: 'Admin',           path: '/admin',                          icon: 'admin',      show: isSuperAdmin,        group: 'MANAGE'   },
  ].filter(item => item.show);

  const navigation = isCandidate ? candidateNav : isServiceProvider ? serviceProviderNav : standardNav;

  const groups = ['PLATFORM', 'TOOLS', 'MANAGE'];

  // Where a manager belongs before they can use the platform:
  //   application still in flight  -> /application-status
  //   no application and no firm   -> /apply (the new entry point)
  //   approved but still no firm   -> /onboarding to create the firm record
  // Team members who joined someone else's firm own no firm row, so check
  // membership before deciding they still need to set one up.
  useEffect(() => {
    if (profileLoading) return;
    if (!profile) { setGate(null); return; }
    let cancelled = false;

    // Allocators, service providers and candidates go through their own
    // application flow, so a pending one holds them in the portal.
    if (profile.role !== 'manager') {
      (async () => {
        const found = await findOwnApplication();
        if (cancelled) return;
        setGate(found && ONBOARDING_PENDING_STATUSES.includes(found.status)
          ? `/application-status?type=${found.kind}`
          : null);
      })();
      return () => { cancelled = true; };
    }

    (async () => {
      // Matched on contact_email: the application is created before the
      // profile link exists, so email is the reliable join for a returning
      // applicant. RLS scopes this to their own rows either way.
      const { data: app } = await supabase.from('manager_applications')
        .select('status').ilike('contact_email', profile.email)
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (cancelled) return;
      const status = (app as { status?: string } | null)?.status;
      // 'contract_signed' is still in flight, signed, but awaiting activation.
      if (status && status !== 'approved') {
        setGate('/application-status?type=manager'); return;
      }
      if (firm) { setGate(null); return; }
      const { data: member } = await supabase
        .from('firm_members').select('firm_id').eq('user_id', profile.id).limit(1);
      if (cancelled) return;
      if (member?.length) { setGate(null); return; }
      setGate(app ? '/onboarding' : '/apply');
    })();
    return () => { cancelled = true; };
  }, [profileLoading, profile, firm]);

  useEffect(() => {
    if (!firm && !isSuperAdmin) return;
    async function fetchCounts() {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return;
      if (firm) {
        const { count: pending } = await supabase
          .from('connections').select('*', { count: 'exact', head: true })
          .eq('manager_firm_id', firm!.id).eq('status', 'pending');
        setPendingCount(pending || 0);

        const { data: firmConnections } = await supabase
          .from('connections').select('id').eq('status', 'approved')
          .or(`allocator_firm_id.eq.${firm!.id},manager_firm_id.eq.${firm!.id}`);

        if (!firmConnections?.length) {
          setUnreadCount(0);
        } else {
          const { count: unread } = await supabase
            .from('messages').select('*', { count: 'exact', head: true })
            .in('connection_id', firmConnections.map(c => c.id))
            .neq('sender_id', user.id).is('read_at', null);
          setUnreadCount(unread || 0);
        }

        const { count: pendingMeetings } = await supabase
          .from('meetings').select('*', { count: 'exact', head: true })
          .eq('recipient_firm_id', firm!.id).eq('status', 'pending');
        setPendingMeetingCount(pendingMeetings || 0);

        const { count: unreadNotifs } = await supabase
          .from('notifications').select('*', { count: 'exact', head: true })
          .eq('user_id', user.id).is('read_at', null);
        setUnreadNotifCount(unreadNotifs || 0);
      }
      if (isSuperAdmin) {
        const { count: pendingAdmin } = await supabase
          .from('profiles').select('*', { count: 'exact', head: true })
          .eq('account_status', 'pending_review');
        setPendingAdminCount(pendingAdmin || 0);

        // Applications still waiting on the admin team.
        const { count: pendingApps } = await supabase
          .from('manager_applications').select('*', { count: 'exact', head: true })
          .in('status', ['submitted', 'regulatory_check', 'admin_review']);
        setPendingApplicationsCount(pendingApps || 0);
      }
    }
    fetchCounts();
    const interval = setInterval(fetchCounts, 5000);
    const channel = supabase.channel('sidebar-counts')
      .on('postgres_changes', { event: '*',      schema: 'public', table: 'connections'   }, fetchCounts)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages'      }, fetchCounts)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages'      }, fetchCounts)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'meetings'      }, fetchCounts)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'meetings'      }, fetchCounts)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, fetchCounts)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications' }, fetchCounts)
      .on('postgres_changes', { event: '*',      schema: 'public', table: 'profiles'      }, fetchCounts)
      .subscribe();
    return () => { clearInterval(interval); supabase.removeChannel(channel); };
  }, [firm, isSuperAdmin]);

  // Service-provider sidebar: own provider id + pending-connection / unread-message badges.
  useEffect(() => {
    if (!isServiceProvider || !profile) return;
    let cancelled = false;
    async function fetchSpCounts() {
      const { data: provider } = await supabase
        .from('service_providers').select('id').eq('owner_id', profile!.id).maybeSingle();
      if (cancelled) return;
      if (!provider) { setProviderId(null); return; }
      setProviderId(provider.id);

      const { count: pending } = await supabase
        .from('service_connections').select('*', { count: 'exact', head: true })
        .eq('provider_id', provider.id).eq('status', 'pending');
      if (!cancelled) setSpPendingCount(pending || 0);

      const { data: conns } = await supabase
        .from('service_connections').select('id')
        .eq('provider_id', provider.id).eq('status', 'approved');
      if (cancelled) return;
      if (!conns?.length) { setSpUnreadCount(0); return; }
      const { count: unread } = await supabase
        .from('service_messages').select('*', { count: 'exact', head: true })
        .in('connection_id', conns.map(c => c.id))
        .neq('sender_id', profile!.id).is('read_at', null);
      if (!cancelled) setSpUnreadCount(unread || 0);
    }
    fetchSpCounts();
    const interval = setInterval(fetchSpCounts, 5000);
    const channel = supabase.channel('sp-sidebar-counts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'service_connections' }, fetchSpCounts)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'service_messages' }, fetchSpCounts)
      .subscribe();
    return () => { cancelled = true; clearInterval(interval); supabase.removeChannel(channel); };
  }, [isServiceProvider, profile]);

  const handleCalendarClick = async () => {
    if (isManager && unreadNotifCount > 0) {
      const user = (await supabase.auth.getUser()).data.user;
      if (user) {
        await supabase.from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', user.id).is('read_at', null);
        setUnreadNotifCount(0);
      }
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()
    : '??';

  // For service-provider nav items that share a pathname but differ by ?tab=,
  // resolve active state from the query string instead of NavLink's pathname match.
  const forcedActive = (path: string): boolean | null => {
    // Admin and Applications share the /admin pathname and differ only by
    // ?tab=. NavLink matches on pathname alone, so without this both light up.
    if (path === '/admin' || path === '/admin?tab=applications') {
      if (location.pathname !== '/admin') return false;
      const curTab = new URLSearchParams(location.search).get('tab');
      return path === '/admin?tab=applications'
        ? curTab === 'applications'
        : curTab !== 'applications';
    }
    if (!isServiceProvider && !isCandidate) return null;
    const [base, query] = path.split('?');
    if (location.pathname !== base) return false;
    const curTab = new URLSearchParams(location.search).get('tab');
    const wantTab = query ? new URLSearchParams(query).get('tab') : null;
    if (wantTab) return curTab === wantTab;
    // No tab on this item (Dashboard / My Profile): active only when no tab param.
    if (base === '/service-provider-dashboard' || base === '/candidate-dashboard') return !curTab;
    return null;
  };

  const getBadge = (path: string) => {
    if (isServiceProvider) {
      if (path.startsWith('/service-provider-dashboard?tab=connections') && spPendingCount > 0) return spPendingCount;
      if (path.startsWith('/service-provider-dashboard?tab=messages') && spUnreadCount > 0) return spUnreadCount;
      return null;
    }
    if (path === '/manage-investors' && pendingCount > 0)    return pendingCount;
    if (path === '/messages'         && unreadCount > 0)     return unreadCount;
    if (path === '/calendar') {
      if (isAllocator && pendingMeetingCount > 0) return pendingMeetingCount;
      if (isManager   && unreadNotifCount > 0)    return unreadNotifCount;
    }
    if (path === '/admin' && pendingAdminCount > 0) return pendingAdminCount;
    if (path === '/admin?tab=applications' && pendingApplicationsCount > 0) return pendingApplicationsCount;
    return null;
  };

  if (gate) return <Navigate to={gate} replace />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F6F8', alignItems: 'flex-start' }}>

      {/* ── Sidebar, width from index.css .altveno-sidebar ── */}
      <aside className="altveno-sidebar">

        {/* Logo */}
        <div style={{
          padding: '18px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '5px' }}>
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#0D6E8A" strokeWidth="2.2" strokeLinecap="round"/>
              <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round"/>
              <circle cx="14" cy="14" r="2.2" fill={C.gold}/>
            </svg>
            <span style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '17px', color: C.white,
              letterSpacing: '-0.2px', fontWeight: 400,
            }}>Altveno</span>
          </div>
          <div style={{
            fontSize: '9px', color: C.gold,
            letterSpacing: '1.8px', fontWeight: 600,
            fontFamily: 'system-ui, sans-serif',
            textTransform: 'uppercase' as const,
          }}>
            {isManager ? 'Manager Platform' : isServiceProvider ? 'Provider Platform' : isCandidate ? 'Candidate Platform' : isSuperAdmin ? 'Admin' : 'Allocator Platform'}
          </div>
        </div>

        {/* Nav */}
        <nav className="altveno-sidebar-nav">
          {groups.map(group => {
            const items = navigation.filter(n => n.group === group);
            if (!items.length) return null;
            // TOOLS collapses to an unlabelled block when it holds fewer than 3 items.
            const showLabel = group !== 'TOOLS' || items.length >= 3;
            return (
              <div key={group} style={{ marginBottom: '6px' }}>
                <div style={{
                  fontSize: '9px', color: C.sideMuted,
                  padding: showLabel ? '10px 16px 4px' : '0',
                  letterSpacing: '1.5px', fontWeight: 700,
                  fontFamily: 'system-ui, sans-serif',
                  textTransform: 'uppercase' as const,
                  borderTop: group !== 'PLATFORM' ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  marginTop: group !== 'PLATFORM' ? '4px' : '0',
                  marginBottom: showLabel ? '0' : '6px',
                }}>
                  {showLabel ? group : null}
                </div>
                {items.map(item => {
                  const badge = getBadge(item.path);
                  const forced = forcedActive(item.path);
                  return (
                    <NavLink
                      key={item.path + item.name}
                      to={item.path}
                      onClick={item.path === '/calendar' ? handleCalendarClick : undefined}
                      style={({ isActive: navActive }) => {
                       const isActive = forced !== null ? forced : navActive;
                       return ({
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '6px 16px 6px 14px',
                        marginBottom: '1px', textDecoration: 'none',
                        fontSize: '12.5px', fontFamily: 'system-ui, sans-serif',
                        background: isActive ? C.sideActive : 'transparent',
                        color: isActive ? C.gold : C.sideText,
                        fontWeight: isActive ? 600 : 400,
                        borderLeft: isActive ? `2px solid ${C.gold}` : '2px solid transparent',
                        transition: 'all 0.1s',
                        letterSpacing: '0.1px',
                      });
                      }}
                      onMouseEnter={e => {
                        const el = e.currentTarget;
                        if (el.style.borderLeftColor !== C.gold) {
                          el.style.background = C.sideHover;
                          el.style.color = C.white;
                        }
                      }}
                      onMouseLeave={e => {
                        const el = e.currentTarget;
                        if (el.style.borderLeftColor !== C.gold) {
                          el.style.background = 'transparent';
                          el.style.color = C.sideText;
                        }
                      }}
                    >
                      <Icon name={item.icon} size={13} color="currentColor" />
                      <span style={{ flex: 1 }}>{item.name}</span>
                      {(item as { soon?: boolean }).soon && (
                        <span style={{
                          background: 'rgba(201,168,76,0.15)', color: C.gold,
                          fontSize: '8.5px', fontWeight: 700,
                          padding: '1px 5px', borderRadius: '2px',
                          border: '1px solid rgba(201,168,76,0.3)',
                          fontFamily: 'system-ui, sans-serif',
                          letterSpacing: '0.5px', textTransform: 'uppercase',
                        }}>Soon</span>
                      )}
                      {badge !== null && (
                        <span style={{
                          background: C.gold, color: C.navy,
                          fontSize: '9px', fontWeight: 700,
                          padding: '1px 5px', borderRadius: '2px',
                          minWidth: '16px', textAlign: 'center',
                          fontFamily: 'system-ui, sans-serif',
                          letterSpacing: '0.3px',
                        }}>{badge}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div style={{
          padding: '10px 16px 14px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '9px',
            marginBottom: '8px',
          }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '3px',
              background: profile?.avatar_url ? 'transparent' : `linear-gradient(135deg, ${C.gold}, #0D9488)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, overflow: 'hidden',
              border: `1px solid rgba(201,168,76,0.3)`,
            }}>
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '10px', fontWeight: 700, color: C.navy, fontFamily: 'system-ui' }}>{initials}</span>
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '12px', fontWeight: 600, color: C.white,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontFamily: 'system-ui, sans-serif',
              }}>
                {profile?.full_name || 'User'}
              </div>
              <div style={{
                fontSize: '10.5px', color: C.sideMuted,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontFamily: 'system-ui, sans-serif',
              }}>
                {firm?.name || 'No firm yet'}
              </div>
            </div>
          </div>
          <button
            onClick={signOut}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '3px', color: C.sideMuted,
              fontSize: '11.5px', padding: '6px',
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
              letterSpacing: '0.3px',
              transition: 'all 0.1s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = C.gold;
              (e.currentTarget as HTMLButtonElement).style.color = C.gold;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)';
              (e.currentTarget as HTMLButtonElement).style.color = C.sideMuted;
            }}
          >
            Sign out
          </button>

          {/* Legal links, standalone public pages outside this layout. */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: '10px',
            marginTop: '9px', fontSize: '10.5px',
            fontFamily: 'system-ui, sans-serif',
          }}>
            <Link to="/privacy" style={{ color: C.sideMuted, textDecoration: 'none' }}>Privacy Policy</Link>
            <span style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            <Link to="/terms" style={{ color: C.sideMuted, textDecoration: 'none' }}>Terms</Link>
          </div>
        </div>
      </aside>

      {/* ── Main, margin from index.css .altveno-sidebar-main ── */}
      <main className="altveno-sidebar-main">

        {/* Top bar */}
        <div style={{
          height: 'var(--header-h)', flexShrink: 0,
          background: C.navy,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center',
          padding: '0 28px',
          position: 'sticky', top: 0, zIndex: 50,
        }}>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              fontSize: '11.5px', color: 'rgba(255,255,255,0.45)',
              fontFamily: 'system-ui, sans-serif',
            }}>
              {firm?.name}
            </div>
            <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.1)' }} />
            <div style={{
              fontSize: '11.5px', color: 'rgba(255,255,255,0.75)',
              fontFamily: 'system-ui, sans-serif', fontWeight: 500,
            }}>
              {profile?.full_name}
            </div>
            <div style={{
              width: '26px', height: '26px', borderRadius: '3px',
              background: profile?.avatar_url ? 'transparent' : `linear-gradient(135deg, ${C.gold}, #0D9488)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', fontWeight: 700, color: C.navy,
              overflow: 'hidden', cursor: 'pointer',
              border: '1px solid rgba(201,168,76,0.3)',
            }}>
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontFamily: 'system-ui' }}>{initials}</span>
              }
            </div>
          </div>
        </div>

        {/* Page content */}
        <div style={{ flex: 1, background: '#F5F6F8' }}>
          <Outlet />
        </div>
      </main>

      <NotificationToast />
      <DemoSwitcher />
    </div>
  );
}